import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { categories, movies } from "@/mocks/movies";

const categoryNames: Record<string, string> = {
  action: "动作片",
  comedy: "喜剧片",
  romance: "爱情片",
  "sci-fi": "科幻片",
  horror: "恐怖片",
  animation: "动画片",
  drama: "剧情片",
  "tv-series": "电视剧",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "action");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "instant", block: "start" });
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredMovies = movies.filter((m) => m.category === activeCategory);

  const categoryName = currentCategory?.name || activeCategory;
  const categoryDesc = currentCategory?.description || "";

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${categoryName} - 丁香花影院`,
    description: `丁香花影院${categoryName}分类，${categoryDesc}。海量${categoryName}在线免费观看，高清流畅播放。`,
    url: `https://www.lilaccinema.com/category/${activeCategory}`,
    isPartOf: {
      "@type": "WebSite",
      name: "丁香花影院",
      url: "https://www.lilaccinema.com",
    },
  };

  return (
    <>
      <Seo
        title={`${categoryName} - 丁香花影院 | 免费在线观看高清${categoryName}`}
        description={`丁香花影院${categoryName}频道，${categoryDesc}。提供最新最全的${categoryName}在线观看，高清画质流畅不卡顿，每日更新热门${categoryName}资源。`}
        keywords={`${categoryName},在线${categoryName},免费${categoryName},高清${categoryName},丁香花影院,在线影院`}
        canonicalUrl={`https://www.lilaccinema.com/category/${activeCategory}`}
        schemaJson={categorySchema}
      />

      <div className="min-h-screen bg-background-50" ref={containerRef}>
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: categoryName },
            ]}
          />

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {categoryName}
              </h1>
              <p className="text-sm md:text-base text-foreground-500">{categoryDesc}</p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? "bg-accent-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard
                    key={movie.id}
                    id={movie.id}
                    title={movie.title}
                    category={movie.category}
                    year={movie.year}
                    rating={movie.rating}
                    imageUrl={movie.imageUrl}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-movie-2-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的影视资源，敬请期待</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}